import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import {
  X, ArrowRight, ArrowLeft, TrendingUp, Leaf, ShieldCheck, Landmark, Search,
  CheckCircle2, Info, Coins, Sprout, Globe2, Building2, Users, Briefcase, Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CHIPS = [500, 1000, 5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000];
const COUNTRIES = [
  'Brazil', 'United States', 'Portugal', 'United Kingdom', 'Spain', 'France', 'Germany', 'Italy',
  'Switzerland', 'Netherlands', 'Belgium', 'Ireland', 'Canada', 'Mexico', 'Argentina', 'Chile',
  'Colombia', 'Peru', 'Uruguay', 'Paraguay', 'United Arab Emirates', 'Qatar', 'Saudi Arabia',
  'Japan', 'China', 'Hong Kong', 'Singapore', 'Australia', 'New Zealand', 'South Africa',
  'India', 'Luxembourg', 'Monaco', 'Angola', 'Mozambique', 'Cape Verde', 'Other',
];

const ST = {
  en: {
    bubble_title: 'INVEST NOW?', bubble_sub: 'From US$ 500', bubble_btn: 'START NOW',
    of: 'of', back: 'Back', close: 'Close', continue: 'Continue', edit: 'Edit', view_details: 'View details',
    live: 'Live Summary', projected: 'Projected — not guaranteed',
    s1_q: 'How much would you like to invest?', s1_custom: 'Custom amount', s1_target: 'YOUR TARGET RANGE',
    s1_monthly: 'Projected monthly target', s1_min: 'Minimum investment is US$ 500',
    s2_q: 'For how long?', months: 'MONTHS', s2_reinvest: 'Reinvest returns?', yes: 'YES', no: 'NO',
    s2_cap: 'Initial Capital', s2_range: 'Target Range', s2_pos: 'Projected Position',
    s3_q: 'Investing as', it: ['Individual', 'Company', 'Family Office', 'Institutional', 'Other'],
    s3_country: 'Where are you investing from?', s3_search: 'Search country', s3_confirm: 'Confirm', s3_change: 'Change',
    s3_contact: 'Your details', name: 'Full name', email: 'Email', phone: 'Phone',
    s4_q1: 'What is your main objective?', obj: ['Monthly Income', 'Growth', 'Diversification', 'Long Term'],
    s4_q2: 'Your investment experience', exp: ['Beginner', 'Intermediate', 'Experienced', 'Professional'],
    s4_q3: 'How important is liquidity?', liq: ['High', 'Medium', 'Low'],
    s4_profile: 'PRELIMINARY INVESTOR PROFILE',
    s4_note: 'Preliminary assessment. Final suitability may require additional information before contracting.',
    profiles: { CONSERVATIVE: 'CONSERVATIVE', BALANCED: 'BALANCED', GROWTH: 'GROWTH' },
    s5_q: 'Review your simulation', r_inv: 'Investment', r_period: 'Period', r_target: 'Target',
    r_reinvest: 'Reinvestment', r_type: 'Investor Type', r_jur: 'Jurisdiction', on: 'ON', off: 'OFF',
    s6_title: 'YOUR MORINGA INVESTMENT', s6_inv: 'INVESTMENT', s6_target: 'TARGET MONTHLY RANGE',
    s6_term: 'TERM', s6_monthly: 'PROJECTED MONTHLY TARGET', s6_results: 'Projected results at end of term',
    base: 'Base scenario', illustrative: 'Illustrative projections. Not guaranteed.',
    eco_title: 'YOUR CAPITAL IS CONNECTED TO', eco_ha: 'Moringa Agricultural Network',
    eco_items: ['Brazil', 'Private Funding Structure', 'Commodity Production', 'Global Export Operations'],
    eco_btn: 'Explore Structure',
    sec_title: 'INVESTMENT STRUCTURE',
    sec_items: ['Single Account', 'Private Funding', 'Agricultural Operations', 'Commodity', 'Documentation', 'Compliance Review'],
    reg_title: 'REGULATORY REVIEW', reg_status: 'Preliminary assessment completed. Final legal/compliance review required before contracting.',
    reg_details: 'Regulatory details',
    risk_title: 'KEY RISKS', risk_items: ['Agricultural', 'Liquidity', 'Commodity', 'Commercial', 'Regulatory'],
    risk_details: 'Risk details',
    comp_identity: 'Identity', comp_jur: 'Jurisdiction', comp_suit: 'Suitability', comp_aml: 'AML/KYC',
    comp_pending: 'Pending', comp_prelim: 'Preliminary Compatible', comp_req: 'Required before contracting',
    pf_title: 'PRIVATE FUNDING', pf_sub: 'Moringa Bank — Funding Structure',
    cta_ready: 'Ready to continue?', cta_open_sub: 'Open your free Single Account to continue your investment.',
    cta_setup: 'SINGLE ACCOUNT SETUP', cta_open: 'OPEN FREE SINGLE ACCOUNT', cta_continue: 'CONTINUE WITH MY SINGLE ACCOUNT',
    err_name: 'Please enter your name', err_email: 'Please enter a valid email', err_phone: 'Please enter your phone',
    sim_id: 'Simulation',
  },
  pt: {
    bubble_title: 'INVESTIR AGORA?', bubble_sub: 'A partir de US$ 500', bubble_btn: 'SIMULAR AGORA',
    of: 'de', back: 'Voltar', close: 'Fechar', continue: 'Continuar', edit: 'Editar', view_details: 'Ver detalhes',
    live: 'Resumo ao Vivo', projected: 'Projetado — não garantido',
    s1_q: 'Quanto você quer investir?', s1_custom: 'Outro valor', s1_target: 'SUA FAIXA ALVO',
    s1_monthly: 'Alvo mensal projetado', s1_min: 'Investimento mínimo é US$ 500',
    s2_q: 'Por quanto tempo?', months: 'MESES', s2_reinvest: 'Reinvestir os retornos?', yes: 'SIM', no: 'NÃO',
    s2_cap: 'Capital Inicial', s2_range: 'Faixa Alvo', s2_pos: 'Posição Projetada',
    s3_q: 'Investindo como', it: ['Individual', 'Empresa', 'Family Office', 'Institucional', 'Outro'],
    s3_country: 'De onde você está investindo?', s3_search: 'Buscar país', s3_confirm: 'Confirmar', s3_change: 'Alterar',
    s3_contact: 'Seus dados', name: 'Nome completo', email: 'Email', phone: 'Telefone',
    s4_q1: 'Qual é o seu principal objetivo?', obj: ['Renda Mensal', 'Crescimento', 'Diversificação', 'Longo Prazo'],
    s4_q2: 'Sua experiência de investimento', exp: ['Iniciante', 'Intermediário', 'Experiente', 'Profissional'],
    s4_q3: 'Qual a importância da liquidez?', liq: ['Alta', 'Média', 'Baixa'],
    s4_profile: 'PERFIL PRELIMINAR DO INVESTIDOR',
    s4_note: 'Avaliação preliminar. A adequação final pode exigir informações adicionais antes da contratação.',
    profiles: { CONSERVATIVE: 'CONSERVADOR', BALANCED: 'BALANCEADO', GROWTH: 'CRESCIMENTO' },
    s5_q: 'Revise sua simulação', r_inv: 'Investimento', r_period: 'Período', r_target: 'Alvo',
    r_reinvest: 'Reinvestimento', r_type: 'Tipo de Investidor', r_jur: 'Jurisdição', on: 'LIGADO', off: 'DESLIGADO',
    s6_title: 'SEU INVESTIMENTO MORINGA', s6_inv: 'INVESTIMENTO', s6_target: 'FAIXA MENSAL ALVO',
    s6_term: 'PRAZO', s6_monthly: 'ALVO MENSAL PROJETADO', s6_results: 'Resultados projetados ao fim do prazo',
    base: 'Cenário base', illustrative: 'Projeções ilustrativas. Não garantidas.',
    eco_title: 'SEU CAPITAL ESTÁ CONECTADO A', eco_ha: 'Rede Agrícola Moringa',
    eco_items: ['Brasil', 'Estrutura de Private Funding', 'Produção de Commodity', 'Operações de Exportação Global'],
    eco_btn: 'Explorar Estrutura',
    sec_title: 'ESTRUTURA DO INVESTIMENTO',
    sec_items: ['Single Account', 'Private Funding', 'Operações Agrícolas', 'Commodity', 'Documentação', 'Revisão de Compliance'],
    reg_title: 'REVISÃO REGULATÓRIA', reg_status: 'Avaliação preliminar concluída. Revisão jurídica/compliance final exigida antes da contratação.',
    reg_details: 'Detalhes regulatórios',
    risk_title: 'PRINCIPAIS RISCOS', risk_items: ['Agrícola', 'Liquidez', 'Commodity', 'Comercial', 'Regulatório'],
    risk_details: 'Detalhes de risco',
    comp_identity: 'Identidade', comp_jur: 'Jurisdição', comp_suit: 'Adequação', comp_aml: 'PLD/KYC',
    comp_pending: 'Pendente', comp_prelim: 'Compatível (preliminar)', comp_req: 'Exigido antes da contratação',
    pf_title: 'PRIVATE FUNDING', pf_sub: 'Moringa Bank — Estrutura de Funding',
    cta_ready: 'Pronto para continuar?', cta_open_sub: 'Abra sua Single Account gratuitamente para continuar seu investimento.',
    cta_setup: 'SETUP DA SINGLE ACCOUNT', cta_open: 'ABRIR SINGLE ACCOUNT GRÁTIS', cta_continue: 'CONTINUAR COM MINHA SINGLE ACCOUNT',
    err_name: 'Informe seu nome', err_email: 'Informe um email válido', err_phone: 'Informe seu telefone',
    sim_id: 'Simulação',
  },
};

const IT_ICONS = [Users, Building2, Briefcase, Landmark, Sparkles];

const fUSD = (v) => '$' + Math.round(Number(v) || 0).toLocaleString('en-US');
const chipLabel = (v) => (v >= 1000000 ? '$1M+' : v >= 1000 ? `$${v / 1000}K` : `$${v}`);

function projection(amount, months, reinvest) {
  const rates = [2, 2.5, 3];
  const vals = rates.map(() => amount);
  const data = [];
  for (let m = 0; m <= months; m++) {
    data.push({ m: `${m}`, cap: amount, p2: Math.round(vals[0]), p25: Math.round(vals[1]), p3: Math.round(vals[2]) });
    for (let i = 0; i < rates.length; i++) {
      if (reinvest) vals[i] *= 1 + rates[i] / 100;
      else vals[i] += (amount * rates[i]) / 100;
    }
  }
  return data;
}

function computeProfile(a, ST_lang) {
  const expIdx = Math.max(0, (ST_lang.exp || []).indexOf(a.experience));
  const liqScore = a.liquidity === ST_lang.liq[0] ? 0 : a.liquidity === ST_lang.liq[1] ? 1 : 2;
  const objScore = a.objective === ST_lang.obj[0] ? 0 : a.objective === ST_lang.obj[2] ? 1 : 2;
  const total = expIdx + liqScore + objScore;
  if (total <= 2) return 'CONSERVATIVE';
  if (total <= 4) return 'BALANCED';
  return 'GROWTH';
}

/* ---- Animated USD counter ---- */
const AnimatedUSD = ({ value }) => {
  const [disp, setDisp] = useState(value);
  const ref = useRef(value);
  useEffect(() => {
    const from = ref.current;
    const to = value;
    const start = performance.now();
    const dur = 420;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisp(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else ref.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{fUSD(disp)}</>;
};

/* ---- Bottom sheet for progressive disclosure ---- */
const DetailSheet = ({ open, title, text, onClose }) => (
  <AnimatePresence>
    {open && (
      <motion.div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
          transition={{ type: 'spring', damping: 26, stiffness: 300 }}
          className="relative z-10 w-full sm:max-w-lg max-h-[80vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-[#e9c767]/20 bg-[#0a1f15] p-6"
          data-testid="moringa-detail-sheet"
        >
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-[#e9c767]">{title}</h4>
            <button onClick={onClose} className="text-emerald-100/60 hover:text-white" data-testid="detail-sheet-close"><X className="w-5 h-5" /></button>
          </div>
          <p className="text-sm leading-relaxed text-emerald-100/80 whitespace-pre-line">{text}</p>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const stepVariants = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

export default function MoringaSimulator({ cfg, user }) {
  const { language, setLanguage } = useLanguage();
  const lang = language === 'pt' ? 'pt' : 'en';
  const s = ST[lang];
  const navigate = useNavigate();

  const minInv = cfg?.min_investment || 500;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [sheet, setSheet] = useState(null); // 'reg' | 'risk' | null
  const [q4, setQ4] = useState(0); // sub-question index for step 4
  const [countrySearch, setCountrySearch] = useState('');
  const [editCountry, setEditCountry] = useState(false);
  const [errors, setErrors] = useState({});
  const refRef = useRef(null);

  const [a, setA] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('moringa_sim') || '{}');
      return {
        amount: saved.amount || 500, period: saved.period || null, reinvest: saved.reinvest || false,
        investorType: saved.investorType || null, country: saved.country || (user?.country || ''),
        objective: saved.objective || null, experience: saved.experience || null, liquidity: saved.liquidity || null,
        name: saved.name || '', email: saved.email || '', phone: saved.phone || '',
      };
    } catch {
      return { amount: 500, period: null, reinvest: false, investorType: null, country: user?.country || '', objective: null, experience: null, liquidity: null, name: '', email: '', phone: '' };
    }
  });

  // resume after signup
  useEffect(() => {
    const resume = localStorage.getItem('moringa_sim_resume');
    if (resume && user) {
      setOpen(true); setStep(6);
      localStorage.removeItem('moringa_sim_resume');
    }
  }, [user]);

  const upd = useCallback((patch) => setA((p) => {
    const next = { ...p, ...patch };
    localStorage.setItem('moringa_sim', JSON.stringify(next));
    return next;
  }), []);

  const profile = useMemo(() => (a.objective && a.experience && a.liquidity ? computeProfile(a, s) : null), [a, s]);
  const months = a.period || 12;
  const chart = useMemo(() => projection(a.amount, months, a.reinvest), [a.amount, months, a.reinvest]);
  const endVals = chart[chart.length - 1] || { p2: a.amount, p25: a.amount, p3: a.amount };

  const saveSim = useCallback(async (status) => {
    const payload = {
      ref: refRef.current, amount_usd: a.amount, period_months: a.period, reinvest: a.reinvest,
      scenario: '2.5', investor_type: a.investorType, country: a.country, objective: a.objective,
      experience: a.experience, liquidity: a.liquidity, preliminary_profile: profile,
      name: a.name || undefined, email: a.email || undefined, phone: a.phone || undefined,
      language: lang, status,
    };
    try {
      const token = localStorage.getItem('youse_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API_URL}/moringa/simulations`, payload, { headers });
      refRef.current = res.data.ref;
      localStorage.setItem('moringa_sim', JSON.stringify({ ...a, ref: res.data.ref }));
      return res.data.ref;
    } catch (e) {
      return refRef.current;
    }
  }, [a, profile, lang]);

  const goNext = useCallback(() => setStep((p) => Math.min(6, p + 1)), []);
  const goBack = useCallback(() => { if (step === 1) { setOpen(false); } else setStep((p) => p - 1); }, [step]);

  const validateContact = () => {
    if (user) return true;
    const e = {};
    if (!a.name.trim()) e.name = s.err_name;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email)) e.email = s.err_email;
    if (!a.phone.trim() || a.phone.replace(/\D/g, '').length < 8) e.phone = s.err_phone;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleStep3Continue = async () => {
    if (!a.investorType || !a.country) return;
    if (!validateContact()) return;
    await saveSim('SIMULATED');
    goNext();
  };

  const selectChip = (v) => upd({ amount: Math.max(minInv, v) });

  const pickPeriod = (m) => upd({ period: m });

  const answerQ4 = (field, val) => {
    upd({ [field]: val });
    setTimeout(() => {
      if (q4 < 2) setQ4((x) => x + 1);
    }, 220);
  };

  const ctaOpen = async () => {
    await saveSim('CONTINUED');
    if (user) {
      navigate('/moringa-dashboard');
    } else {
      localStorage.setItem('moringa_sim_resume', '1');
      navigate('/signup');
    }
    setOpen(false);
  };

  const exploreStructure = () => {
    setOpen(false);
    setTimeout(() => document.getElementById('ecosystem')?.scrollIntoView({ behavior: 'smooth' }), 150);
  };

  const monthlyLow = Math.round((a.amount * 2) / 100);
  const monthlyHigh = Math.round((a.amount * 3) / 100);

  const filteredCountries = COUNTRIES.filter((c) => c.toLowerCase().includes(countrySearch.toLowerCase()));

  return (
    <>
      {/* FLOATING BUBBLE */}
      {!open && (
        <motion.button
          onClick={() => { setOpen(true); setStep(1); }}
          initial={{ opacity: 0, scale: 0.6, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.8, type: 'spring', stiffness: 260, damping: 18 }}
          whileHover={{ scale: 1.04 }}
          className="fixed z-[90] bottom-5 right-5 sm:bottom-7 sm:right-7 flex items-center gap-3 rounded-2xl border border-[#e9c767]/40 bg-[#07160f]/95 backdrop-blur-xl pl-4 pr-5 py-3 shadow-[0_8px_40px_rgba(233,199,103,0.3)]"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          data-testid="moringa-invest-bubble"
        >
          <span className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#e9c767] to-[#b8902f]">
            <Sprout className="w-5 h-5 text-[#07160f]" />
            <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 animate-ping" />
            <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400" />
          </span>
          <span className="text-left">
            <span className="block text-sm font-extrabold tracking-wide text-[#e9c767] leading-none">{s.bubble_title}</span>
            <span className="block text-[11px] text-emerald-100/70 mt-0.5">{s.bubble_sub}</span>
          </span>
          <span className="ml-1 rounded-full bg-[#e9c767] px-3 py-1.5 text-[11px] font-bold text-[#07160f] whitespace-nowrap">{s.bubble_btn}</span>
        </motion.button>
      )}

      {/* MODAL */}
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[100] flex items-stretch sm:items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-[#030a06]/85 backdrop-blur-md" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="relative z-10 flex w-full sm:max-w-5xl sm:mx-4 h-full sm:h-auto sm:max-h-[92vh] flex-col overflow-hidden bg-[#07160f] sm:rounded-3xl border border-[#e9c767]/20 text-white font-[Outfit,sans-serif]"
              data-testid="moringa-simulator-modal"
            >
              {/* HEADER */}
              <div className="flex items-center justify-between gap-3 border-b border-[#e9c767]/15 px-5 py-4" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
                <button onClick={goBack} className="flex items-center gap-1.5 text-sm text-emerald-100/70 hover:text-[#e9c767]" data-testid="sim-back-btn">
                  <ArrowLeft className="w-4 h-4" /> {s.back}
                </button>
                <div className="flex-1 mx-2">
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-[11px] font-semibold tracking-widest text-[#e9c767]">{step} {s.of} 6</span>
                  </div>
                  <div className="mt-1.5 h-1 w-full rounded-full bg-[#e9c767]/10 overflow-hidden">
                    <motion.div className="h-full bg-[#e9c767]" animate={{ width: `${(step / 6) * 100}%` }} transition={{ duration: 0.3 }} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setLanguage(lang === 'pt' ? 'en' : 'pt')} className="rounded-full border border-[#e9c767]/30 px-2.5 py-1 text-[11px] font-semibold text-[#e9c767] hover:bg-[#e9c767]/10" data-testid="sim-lang-toggle">{lang === 'pt' ? 'EN' : 'PT'}</button>
                  <button onClick={() => setOpen(false)} className="text-emerald-100/60 hover:text-white" data-testid="sim-close-btn"><X className="w-5 h-5" /></button>
                </div>
              </div>

              {/* BODY */}
              <div className="flex-1 overflow-y-auto px-5 py-6 sm:px-10 sm:py-8" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
                <AnimatePresence mode="wait">
                  {/* ---------- STEP 1 ---------- */}
                  {step === 1 && (
                    <motion.div key="s1" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.22 }} data-testid="sim-step-1">
                      <h3 className="text-2xl md:text-3xl font-extrabold leading-tight">{s.s1_q}</h3>
                      <div className="mt-6 text-center">
                        <div className="text-5xl md:text-6xl font-extrabold text-[#e9c767]" data-testid="sim-amount-display">{fUSD(a.amount)}</div>
                      </div>
                      <input type="range" min={minInv} max={1000000} step={100} value={a.amount}
                        onChange={(e) => upd({ amount: Number(e.target.value) })}
                        className="mt-6 w-full accent-[#e9c767]" data-testid="sim-slider" />
                      <div className="mt-5 flex flex-wrap gap-2">
                        {CHIPS.map((v) => (
                          <button key={v} onClick={() => selectChip(v)}
                            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${a.amount === v ? 'border-[#e9c767] bg-[#e9c767] text-[#07160f]' : 'border-[#e9c767]/25 text-emerald-100/80 hover:border-[#e9c767]/60'}`}
                            data-testid={`sim-chip-${v}`}>{chipLabel(v)}</button>
                        ))}
                      </div>
                      <div className="mt-4">
                        <label className="text-[11px] uppercase tracking-widest text-emerald-200/50">{s.s1_custom}</label>
                        <input type="number" inputMode="numeric" min={minInv} value={a.amount}
                          onChange={(e) => upd({ amount: Number(e.target.value || 0) })}
                          className="mt-1 w-full rounded-xl border border-[#e9c767]/20 bg-[#0a1f15] px-4 py-3 text-lg font-semibold outline-none focus:border-[#e9c767]/60"
                          data-testid="sim-custom-input" />
                        {a.amount < minInv && <p className="mt-1 text-xs text-red-400">{s.s1_min}</p>}
                      </div>
                      <div className="mt-6 rounded-2xl border border-[#e9c767]/20 bg-[#0a1f15] p-5 text-center">
                        <p className="text-[11px] uppercase tracking-widest text-emerald-200/60">{s.s1_target}</p>
                        <p className="mt-1 text-xl font-bold text-white">2% – 3% <span className="text-sm font-medium text-emerald-100/60">/ {lang === 'pt' ? 'mês' : 'month'}</span></p>
                        <p className="mt-4 text-[11px] uppercase tracking-widest text-emerald-200/60">{s.s1_monthly}</p>
                        <p className="mt-1 text-3xl font-extrabold text-[#e9c767]"><AnimatedUSD value={monthlyLow} /> – <AnimatedUSD value={monthlyHigh} /></p>
                        <p className="mt-2 text-[11px] italic text-emerald-200/40">{s.projected}</p>
                      </div>
                      <button disabled={a.amount < minInv} onClick={goNext}
                        className="mt-7 w-full rounded-full bg-[#e9c767] px-7 py-4 text-sm font-bold text-[#07160f] hover:brightness-110 disabled:opacity-40 transition-all"
                        data-testid="sim-s1-continue">{s.continue} <ArrowRight className="inline w-4 h-4 ml-1" /></button>
                    </motion.div>
                  )}

                  {/* ---------- STEP 2 ---------- */}
                  {step === 2 && (
                    <motion.div key="s2" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.22 }} data-testid="sim-step-2">
                      <h3 className="text-2xl md:text-3xl font-extrabold leading-tight">{s.s2_q}</h3>
                      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[12, 24, 36, 60].map((m) => (
                          <button key={m} onClick={() => pickPeriod(m)}
                            className={`rounded-2xl border p-5 text-center transition-all ${a.period === m ? 'border-[#e9c767] bg-[#e9c767]/15' : 'border-[#e9c767]/20 hover:border-[#e9c767]/50'}`}
                            data-testid={`sim-period-${m}`}>
                            <div className="text-3xl font-extrabold text-[#e9c767]">{m}</div>
                            <div className="text-[11px] uppercase tracking-widest text-emerald-100/60 mt-1">{s.months}</div>
                          </button>
                        ))}
                      </div>
                      <div className="mt-5 flex items-center justify-between rounded-2xl border border-[#e9c767]/20 bg-[#0a1f15] p-4">
                        <span className="text-sm font-semibold">{s.s2_reinvest}</span>
                        <div className="flex gap-2">
                          <button onClick={() => upd({ reinvest: true })} className={`rounded-full px-5 py-2 text-xs font-bold ${a.reinvest ? 'bg-[#e9c767] text-[#07160f]' : 'border border-[#e9c767]/30 text-emerald-100/70'}`} data-testid="sim-reinvest-yes">{s.yes}</button>
                          <button onClick={() => upd({ reinvest: false })} className={`rounded-full px-5 py-2 text-xs font-bold ${!a.reinvest ? 'bg-[#e9c767] text-[#07160f]' : 'border border-[#e9c767]/30 text-emerald-100/70'}`} data-testid="sim-reinvest-no">{s.no}</button>
                        </div>
                      </div>
                      {a.period && (
                        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-5 rounded-2xl border border-[#e9c767]/15 bg-[#0a1f15] p-4">
                          <ResponsiveContainer width="100%" height={200}>
                            <AreaChart data={chart}>
                              <defs>
                                <linearGradient id="simg3" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#e9c767" stopOpacity={0.5} /><stop offset="100%" stopColor="#e9c767" stopOpacity={0} /></linearGradient>
                                <linearGradient id="simg2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3ba776" stopOpacity={0.4} /><stop offset="100%" stopColor="#3ba776" stopOpacity={0} /></linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(233,199,103,0.08)" />
                              <XAxis dataKey="m" stroke="rgba(255,255,255,0.4)" fontSize={10} interval={Math.max(0, Math.floor(months / 6) - 1)} />
                              <YAxis stroke="rgba(255,255,255,0.4)" fontSize={10} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                              <Tooltip contentStyle={{ background: '#0a1f15', border: '1px solid rgba(233,199,103,0.3)', borderRadius: 12, color: '#fff' }} formatter={(v) => fUSD(v)} />
                              <Area type="monotone" dataKey="p3" name={s.s2_pos} stroke="#e9c767" strokeWidth={2} fill="url(#simg3)" animationDuration={900} />
                              <Area type="monotone" dataKey="p2" name={s.s2_range} stroke="#3ba776" strokeWidth={2} fill="url(#simg2)" animationDuration={900} />
                            </AreaChart>
                          </ResponsiveContainer>
                          <p className="mt-1 text-[11px] italic text-emerald-200/40">{s.illustrative}</p>
                        </motion.div>
                      )}
                      <button disabled={!a.period} onClick={goNext}
                        className="mt-7 w-full rounded-full bg-[#e9c767] px-7 py-4 text-sm font-bold text-[#07160f] hover:brightness-110 disabled:opacity-40 transition-all"
                        data-testid="sim-s2-continue">{s.continue} <ArrowRight className="inline w-4 h-4 ml-1" /></button>
                    </motion.div>
                  )}

                  {/* ---------- STEP 3 ---------- */}
                  {step === 3 && (
                    <motion.div key="s3" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.22 }} data-testid="sim-step-3">
                      <h3 className="text-2xl md:text-3xl font-extrabold leading-tight">{s.s3_q}</h3>
                      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {s.it.map((t, i) => {
                          const Icon = IT_ICONS[i] || Users;
                          return (
                            <button key={t} onClick={() => upd({ investorType: t })}
                              className={`flex flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-all ${a.investorType === t ? 'border-[#e9c767] bg-[#e9c767]/15' : 'border-[#e9c767]/20 hover:border-[#e9c767]/50'}`}
                              data-testid={`sim-investor-${i}`}>
                              <Icon className="w-5 h-5 text-[#e9c767]" />
                              <span className="text-sm font-semibold">{t}</span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Country */}
                      <div className="mt-6">
                        <p className="text-sm font-semibold">{s.s3_country}</p>
                        {a.country && !editCountry ? (
                          <div className="mt-2 flex items-center justify-between rounded-xl border border-[#e9c767]/25 bg-[#0a1f15] px-4 py-3">
                            <span className="flex items-center gap-2 font-semibold"><Globe2 className="w-4 h-4 text-[#e9c767]" /> {a.country} <CheckCircle2 className="w-4 h-4 text-emerald-400" /></span>
                            <button onClick={() => setEditCountry(true)} className="text-xs text-[#e9c767] underline" data-testid="sim-country-change">{s.s3_change}</button>
                          </div>
                        ) : (
                          <div className="mt-2">
                            <div className="flex items-center gap-2 rounded-xl border border-[#e9c767]/20 bg-[#0a1f15] px-3 py-2">
                              <Search className="w-4 h-4 text-emerald-100/50" />
                              <input value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} placeholder={s.s3_search}
                                className="w-full bg-transparent text-sm outline-none" data-testid="sim-country-search" />
                            </div>
                            <div className="mt-2 max-h-40 overflow-y-auto grid grid-cols-2 gap-1.5">
                              {filteredCountries.map((cn) => (
                                <button key={cn} onClick={() => { upd({ country: cn }); setEditCountry(false); setCountrySearch(''); }}
                                  className={`rounded-lg px-3 py-2 text-left text-sm transition-colors ${a.country === cn ? 'bg-[#e9c767] text-[#07160f]' : 'bg-[#07160f] border border-[#e9c767]/10 text-emerald-100/80 hover:border-[#e9c767]/40'}`}
                                  data-testid={`sim-country-${cn}`}>{cn}</button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Contact (guests only) */}
                      {!user && (
                        <div className="mt-6">
                          <p className="text-sm font-semibold">{s.s3_contact}</p>
                          <div className="mt-2 space-y-3">
                            <div>
                              <input value={a.name} onChange={(e) => upd({ name: e.target.value })} placeholder={s.name}
                                className="w-full rounded-xl border border-[#e9c767]/20 bg-[#0a1f15] px-4 py-3 text-sm outline-none focus:border-[#e9c767]/60" data-testid="sim-name" />
                              {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
                            </div>
                            <div>
                              <input type="email" value={a.email} onChange={(e) => upd({ email: e.target.value })} placeholder={s.email}
                                className="w-full rounded-xl border border-[#e9c767]/20 bg-[#0a1f15] px-4 py-3 text-sm outline-none focus:border-[#e9c767]/60" data-testid="sim-email" />
                              {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email}</p>}
                            </div>
                            <div>
                              <input type="tel" inputMode="tel" value={a.phone} onChange={(e) => upd({ phone: e.target.value })} placeholder={s.phone}
                                className="w-full rounded-xl border border-[#e9c767]/20 bg-[#0a1f15] px-4 py-3 text-sm outline-none focus:border-[#e9c767]/60" data-testid="sim-phone" />
                              {errors.phone && <p className="mt-1 text-xs text-red-400">{errors.phone}</p>}
                            </div>
                          </div>
                        </div>
                      )}

                      <button disabled={!a.investorType || !a.country} onClick={handleStep3Continue}
                        className="mt-7 w-full rounded-full bg-[#e9c767] px-7 py-4 text-sm font-bold text-[#07160f] hover:brightness-110 disabled:opacity-40 transition-all"
                        data-testid="sim-s3-continue">{s.continue} <ArrowRight className="inline w-4 h-4 ml-1" /></button>
                    </motion.div>
                  )}

                  {/* ---------- STEP 4 ---------- */}
                  {step === 4 && (
                    <motion.div key="s4" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.22 }} data-testid="sim-step-4">
                      <AnimatePresence mode="wait">
                        {q4 === 0 && (
                          <motion.div key="q0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}>
                            <h3 className="text-xl md:text-2xl font-extrabold">{s.s4_q1}</h3>
                            <div className="mt-5 grid grid-cols-2 gap-3">
                              {s.obj.map((o) => (
                                <button key={o} onClick={() => answerQ4('objective', o)}
                                  className={`rounded-2xl border p-4 text-sm font-semibold transition-all ${a.objective === o ? 'border-[#e9c767] bg-[#e9c767]/15' : 'border-[#e9c767]/20 hover:border-[#e9c767]/50'}`}
                                  data-testid={`sim-obj-${o}`}>{o}</button>
                              ))}
                            </div>
                          </motion.div>
                        )}
                        {q4 === 1 && (
                          <motion.div key="q1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}>
                            <h3 className="text-xl md:text-2xl font-extrabold">{s.s4_q2}</h3>
                            <div className="mt-5 grid grid-cols-2 gap-3">
                              {s.exp.map((o) => (
                                <button key={o} onClick={() => answerQ4('experience', o)}
                                  className={`rounded-2xl border p-4 text-sm font-semibold transition-all ${a.experience === o ? 'border-[#e9c767] bg-[#e9c767]/15' : 'border-[#e9c767]/20 hover:border-[#e9c767]/50'}`}
                                  data-testid={`sim-exp-${o}`}>{o}</button>
                              ))}
                            </div>
                          </motion.div>
                        )}
                        {q4 === 2 && (
                          <motion.div key="q2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}>
                            <h3 className="text-xl md:text-2xl font-extrabold">{s.s4_q3}</h3>
                            <div className="mt-5 grid grid-cols-3 gap-3">
                              {s.liq.map((o) => (
                                <button key={o} onClick={() => answerQ4('liquidity', o)}
                                  className={`rounded-2xl border p-4 text-sm font-semibold transition-all ${a.liquidity === o ? 'border-[#e9c767] bg-[#e9c767]/15' : 'border-[#e9c767]/20 hover:border-[#e9c767]/50'}`}
                                  data-testid={`sim-liq-${o}`}>{o}</button>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {profile && (
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mt-7 rounded-2xl border border-[#e9c767]/25 bg-[#0a1f15] p-5 text-center" data-testid="sim-profile-result">
                          <p className="text-[11px] uppercase tracking-widest text-emerald-200/60">{s.s4_profile}</p>
                          <p className="mt-1 text-3xl font-extrabold text-[#e9c767]">{s.profiles[profile]}</p>
                          <p className="mt-2 text-[11px] text-emerald-200/40">{s.s4_note}</p>
                        </motion.div>
                      )}

                      <button disabled={!profile} onClick={goNext}
                        className="mt-7 w-full rounded-full bg-[#e9c767] px-7 py-4 text-sm font-bold text-[#07160f] hover:brightness-110 disabled:opacity-40 transition-all"
                        data-testid="sim-s4-continue">{s.continue} <ArrowRight className="inline w-4 h-4 ml-1" /></button>
                    </motion.div>
                  )}

                  {/* ---------- STEP 5 REVIEW ---------- */}
                  {step === 5 && (
                    <motion.div key="s5" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.22 }} data-testid="sim-step-5">
                      <h3 className="text-2xl md:text-3xl font-extrabold">{s.s5_q}</h3>
                      <div className="mt-6 divide-y divide-[#e9c767]/10 rounded-2xl border border-[#e9c767]/20 bg-[#0a1f15]">
                        {[
                          [s.r_inv, fUSD(a.amount)],
                          [s.r_period, `${a.period} ${s.months}`],
                          [s.r_target, '2% – 3%'],
                          [s.r_reinvest, a.reinvest ? s.on : s.off],
                          [s.r_type, a.investorType],
                          [s.r_jur, a.country],
                        ].map(([k, v]) => (
                          <div key={k} className="flex items-center justify-between px-5 py-4">
                            <span className="text-sm text-emerald-100/60">{k}</span>
                            <span className="text-sm font-bold text-white">{v}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-7 flex gap-3">
                        <button onClick={() => setStep(1)} className="flex-1 rounded-full border border-[#e9c767]/40 px-6 py-4 text-sm font-semibold text-[#e9c767] hover:bg-[#e9c767]/10" data-testid="sim-edit-btn">{s.edit}</button>
                        <button onClick={async () => { await saveSim('SIMULATED'); goNext(); }} className="flex-[2] rounded-full bg-[#e9c767] px-6 py-4 text-sm font-bold text-[#07160f] hover:brightness-110" data-testid="sim-s5-continue">{s.continue} <ArrowRight className="inline w-4 h-4 ml-1" /></button>
                      </div>
                    </motion.div>
                  )}

                  {/* ---------- STEP 6 SUMMARY ---------- */}
                  {step === 6 && (
                    <motion.div key="s6" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.22 }} data-testid="sim-step-6">
                      <div className="text-center">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-[#e9c767]">{s.s6_title}</p>
                      </div>
                      <div className="mt-5 grid grid-cols-2 gap-3">
                        <div className="rounded-2xl border border-[#e9c767]/20 bg-[#0a1f15] p-4"><p className="text-[10px] uppercase tracking-widest text-emerald-200/60">{s.s6_inv}</p><p className="mt-1 text-2xl font-extrabold text-white">{fUSD(a.amount)}</p></div>
                        <div className="rounded-2xl border border-[#e9c767]/20 bg-[#0a1f15] p-4"><p className="text-[10px] uppercase tracking-widest text-emerald-200/60">{s.s6_target}</p><p className="mt-1 text-2xl font-extrabold text-[#e9c767]">2% – 3%</p></div>
                        <div className="rounded-2xl border border-[#e9c767]/20 bg-[#0a1f15] p-4"><p className="text-[10px] uppercase tracking-widest text-emerald-200/60">{s.s6_term}</p><p className="mt-1 text-2xl font-extrabold text-white">{a.period} {s.months}</p></div>
                        <div className="rounded-2xl border border-[#e9c767]/20 bg-[#0a1f15] p-4"><p className="text-[10px] uppercase tracking-widest text-emerald-200/60">{s.s6_monthly}</p><p className="mt-1 text-2xl font-extrabold text-[#e9c767]">{fUSD(monthlyLow)} – {fUSD(monthlyHigh)}</p></div>
                      </div>

                      {/* Projected results */}
                      <p className="mt-7 text-[11px] uppercase tracking-widest text-emerald-200/60">{s.s6_results}</p>
                      <div className="mt-3 grid grid-cols-3 gap-3">
                        {[['2%', endVals.p2], ['2.5%', endVals.p25], ['3%', endVals.p3]].map(([r, v], i) => (
                          <div key={r} className={`rounded-2xl border p-4 text-center ${i === 1 ? 'border-[#e9c767] bg-[#e9c767]/10 scale-[1.04]' : 'border-[#e9c767]/20 bg-[#0a1f15]'}`} data-testid={`sim-result-${r}`}>
                            <p className="text-sm font-bold text-[#e9c767]">{r}</p>
                            <p className="mt-1 text-lg font-extrabold text-white">{fUSD(v)}</p>
                            {i === 1 && <p className="mt-1 text-[9px] uppercase tracking-wider text-emerald-200/60">{s.base}</p>}
                          </div>
                        ))}
                      </div>
                      <p className="mt-2 text-[11px] italic text-emerald-200/40">{s.illustrative}</p>

                      {/* Final graph */}
                      <div className="mt-5 rounded-2xl border border-[#e9c767]/15 bg-[#0a1f15] p-4">
                        <div className="mb-3 flex gap-2">
                          {[12, 24, 36, 60].map((m) => (
                            <button key={m} onClick={() => upd({ period: m })} className={`rounded-full px-3 py-1 text-[11px] font-bold ${a.period === m ? 'bg-[#e9c767] text-[#07160f]' : 'border border-[#e9c767]/25 text-emerald-100/70'}`} data-testid={`sim-filter-${m}`}>{m}M</button>
                          ))}
                        </div>
                        <ResponsiveContainer width="100%" height={210}>
                          <AreaChart data={chart}>
                            <defs>
                              <linearGradient id="fin3" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#e9c767" stopOpacity={0.5} /><stop offset="100%" stopColor="#e9c767" stopOpacity={0} /></linearGradient>
                              <linearGradient id="fin2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3ba776" stopOpacity={0.4} /><stop offset="100%" stopColor="#3ba776" stopOpacity={0} /></linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(233,199,103,0.08)" />
                            <XAxis dataKey="m" stroke="rgba(255,255,255,0.4)" fontSize={10} interval={Math.max(0, Math.floor(months / 6) - 1)} />
                            <YAxis stroke="rgba(255,255,255,0.4)" fontSize={10} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                            <Tooltip contentStyle={{ background: '#0a1f15', border: '1px solid rgba(233,199,103,0.3)', borderRadius: 12, color: '#fff' }} formatter={(v) => fUSD(v)} />
                            <Area type="monotone" dataKey="p3" name={s.s2_pos} stroke="#e9c767" strokeWidth={2} fill="url(#fin3)" />
                            <Area type="monotone" dataKey="p2" name={s.s2_range} stroke="#3ba776" strokeWidth={2} fill="url(#fin2)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Ecosystem */}
                      <div className="mt-5 rounded-2xl border border-[#e9c767]/20 bg-[radial-gradient(circle_at_20%_20%,rgba(233,199,103,0.12),transparent_60%)] p-5">
                        <p className="text-[10px] uppercase tracking-widest text-emerald-200/60">{s.eco_title}</p>
                        <p className="mt-1 text-3xl font-extrabold text-[#e9c767]">+{Number(cfg?.total_hectares || 50000).toLocaleString('en-US')} HA</p>
                        <p className="text-sm text-emerald-100/70">{s.eco_ha}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {s.eco_items.map((it) => <span key={it} className="rounded-full border border-[#e9c767]/20 px-3 py-1 text-[11px] text-emerald-100/70">{it}</span>)}
                        </div>
                        <button onClick={exploreStructure} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#e9c767] hover:underline" data-testid="sim-explore-structure">{s.eco_btn} <ArrowRight className="w-4 h-4" /></button>
                      </div>

                      {/* Security structure */}
                      <div className="mt-5 rounded-2xl border border-[#e9c767]/15 bg-[#0a1f15] p-5">
                        <p className="text-[10px] uppercase tracking-widest text-emerald-200/60 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#e9c767]" /> {s.sec_title}</p>
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          {s.sec_items.map((it) => <span key={it} className="flex items-center gap-2 text-sm text-emerald-100/80"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> {it}</span>)}
                        </div>
                      </div>

                      {/* Private funding compact */}
                      <div className="mt-5 flex items-center justify-between rounded-2xl border border-[#e9c767]/15 bg-[#0a1f15] p-5">
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-emerald-200/60 flex items-center gap-2"><Landmark className="w-4 h-4 text-[#e9c767]" /> {s.pf_title}</p>
                          <p className="mt-1 text-sm font-semibold">{s.pf_sub}</p>
                          <p className="text-[11px] text-emerald-400">{cfg?.funding_status || 'Active – Verified'}</p>
                        </div>
                        <Coins className="w-8 h-8 text-[#e9c767]/60" />
                      </div>

                      {/* Compliance + Regulatory + Risk */}
                      <div className="mt-5 grid sm:grid-cols-2 gap-3">
                        <div className="rounded-2xl border border-[#e9c767]/15 bg-[#0a1f15] p-5">
                          <p className="text-[10px] uppercase tracking-widest text-emerald-200/60 mb-3">{s.reg_title}</p>
                          <ul className="space-y-1.5 text-xs text-emerald-100/75">
                            <li className="flex justify-between"><span>{s.comp_identity}</span><span className="text-amber-300">{s.comp_pending}</span></li>
                            <li className="flex justify-between"><span>{s.comp_jur}</span><span className="text-white">{a.country}</span></li>
                            <li className="flex justify-between"><span>{s.comp_suit}</span><span className="text-emerald-400">{s.comp_prelim}</span></li>
                            <li className="flex justify-between"><span>{s.comp_aml}</span><span className="text-amber-300">{s.comp_req}</span></li>
                          </ul>
                          <button onClick={() => setSheet('reg')} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#e9c767] hover:underline" data-testid="sim-reg-details"><Info className="w-3.5 h-3.5" /> {s.view_details}</button>
                        </div>
                        <div className="rounded-2xl border border-[#e9c767]/15 bg-[#0a1f15] p-5">
                          <p className="text-[10px] uppercase tracking-widest text-emerald-200/60 mb-3">{s.risk_title}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {s.risk_items.map((r) => <span key={r} className="rounded-full border border-[#e9c767]/20 px-2.5 py-1 text-[11px] text-emerald-100/70">{r}</span>)}
                          </div>
                          <button onClick={() => setSheet('risk')} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#e9c767] hover:underline" data-testid="sim-risk-details"><Info className="w-3.5 h-3.5" /> {s.view_details}</button>
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="mt-7 rounded-3xl border border-[#e9c767]/30 bg-[radial-gradient(circle_at_30%_20%,rgba(233,199,103,0.15),transparent_60%)] p-6 text-center">
                        <p className="text-xl font-extrabold">{s.cta_ready}</p>
                        {!user && <p className="mt-1 text-sm text-emerald-100/70">{s.cta_open_sub}</p>}
                        {!user && (
                          <div className="mt-4 inline-flex flex-col items-center">
                            <span className="text-[10px] uppercase tracking-widest text-emerald-200/60">{s.cta_setup}</span>
                            <span className="text-3xl font-extrabold text-[#e9c767]">US$ 0</span>
                          </div>
                        )}
                        <button onClick={ctaOpen} className="mt-5 w-full rounded-full bg-[#e9c767] px-7 py-4 text-sm font-bold text-[#07160f] hover:brightness-110 shadow-[0_0_30px_rgba(233,199,103,0.3)] transition-all" data-testid="sim-cta-open">
                          {user ? s.cta_continue : s.cta_open} <ArrowRight className="inline w-4 h-4 ml-1" />
                        </button>
                        {refRef.current && <p className="mt-3 text-[11px] text-emerald-200/40">{s.sim_id}: <span className="font-mono text-[#e9c767]">{refRef.current}</span></p>}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* LIVE SUMMARY BAR (mobile sticky footer, hidden on step 6) */}
              {step < 6 && (
                <div className="border-t border-[#e9c767]/15 bg-[#0a1f15] px-5 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-emerald-200/50">{s.live}</p>
                    <p className="text-sm font-bold text-white">{fUSD(a.amount)}{a.period ? ` · ${a.period}${lang === 'pt' ? 'm' : 'm'}` : ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-widest text-emerald-200/50">{s.s1_monthly}</p>
                    <p className="text-sm font-bold text-[#e9c767]">{fUSD(monthlyLow)} – {fUSD(monthlyHigh)}</p>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <DetailSheet open={sheet === 'reg'} title={s.reg_details} text={(lang === 'pt' ? cfg?.regulatory_details_pt : cfg?.regulatory_details_en) || ''} onClose={() => setSheet(null)} />
      <DetailSheet open={sheet === 'risk'} title={s.risk_details} text={(lang === 'pt' ? cfg?.risk_details_pt : cfg?.risk_details_en) || ''} onClose={() => setSheet(null)} />
    </>
  );
}
